import { isEqual } from 'lodash';
import React, { SyntheticEvent } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { BSModal } from '~/components/Bootstrap';
import { DatePicker } from '~/components/Form';
import { OptionWithDescription, OptionWithDescriptionType, Select } from '~/components/Select';
import { ClinicType, PCPType, SearchFilterType, ServicesContext } from '~/services';
import {
    ReminderContext,
    ReminderContextType,
    ReminderFilterType,
    initialReminderFilters,
} from '~/services/ReminderContext';
import { useReminderService } from '~/services/ReminderService';
import { mapPCPs } from '~/util';

export function ReminderFiltersModal() {
    const [show, setShow] = React.useState(false);
    const servicesContext = React.useContext(ServicesContext);
    const { getTimelyActionMeasures } = useReminderService();
    const {
        filterValues,
        setFilterValues,
        byMe,
        setCreatedByMe,
        resetPagination,
        fieldIsFiltered,
        resetSort,
        clinicList,
        pcpList,
    } = React.useContext(ReminderContext) as ReminderContextType;
    // get saved filters
    const { savedFilters } = servicesContext.user.getSavedSearchData();
    const filterOptions = savedFilters.map((x: SearchFilterType) => ({
        ...x,
        value: x.label,
    }));
    // filter state
    const [_PCPList, _setPCPList] = React.useState(pcpList);
    const [_createdByMe, _setCreatedByMe] = React.useState(byMe);
    const [filterSelection, setFilterSelection] = React.useState<null | SearchFilterType>();
    const [formVals, setFormVals] = React.useState<ReminderFilterType>(
        JSON.parse(JSON.stringify(filterValues))
    );
    const [measureCodeOptions, setMeasureCodeOptions] = React.useState<OptionWithDescriptionType[]>(
        []
    );

    const createdByTypeOptions = [
        { value: null, label: 'All' },
        { value: 'system', label: 'Timely Action' },
        { value: 'user', label: 'User Created' },
    ];

    const statusOptions = [
        { value: 'open', label: 'Open' },
        { value: 'inprogress', label: 'In Progress' },
        { value: 'done', label: 'Done' },
        { value: 'na', label: 'N/A' },
        { value: 'missed', label: 'Missed' },
    ];

    // compute filter count
    let filterCount = 0;
    if (
        fieldIsFiltered('dueDateStart', formVals.dueDateStart) ||
        fieldIsFiltered('dueDateEnd', formVals.dueDateEnd)
    )
        filterCount += 1;
    if (formVals.clinic !== null && formVals.clinic.length > 0) filterCount += 1;
    if (formVals.PCP !== null && formVals.PCP.length > 0) filterCount += 1;
    if (_createdByMe === true) filterCount += 1;
    if (formVals.createdByType !== null) filterCount += 1;
    if (formVals.viewStatus.length > 0) filterCount += 1;
    if (formVals.measureCode !== null && formVals.measureCode.length > 0) filterCount += 1;

    React.useEffect(() => {
        const fetchTimelyActionMeasures = async () => {
            const measures = await getTimelyActionMeasures();
            setMeasureCodeOptions(
                measures
                    .map((x) => ({
                        value: x.code,
                        label: x.short_name,
                        description: x.description,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label))
            );
        };
        if (show && measureCodeOptions.length === 0) {
            fetchTimelyActionMeasures();
        }
    }, [getTimelyActionMeasures, show, measureCodeOptions.length]);

    // Update the PCP list any time the clinic changes
    React.useEffect(() => {
        _setPCPList(
            mapPCPs(
                formVals.clinic !== null && formVals.clinic.length > 0
                    ? formVals.clinic
                    : clinicList
            )
        );
    }, [formVals.clinic, clinicList]);

    function updateFormVal(updateObj: Partial<ReminderFilterType>) {
        setFormVals((prev) => ({ ...prev, ...updateObj }));
    }

    function resetFormVals() {
        _setPCPList(pcpList);
        _setCreatedByMe(byMe);
        setFormVals(JSON.parse(JSON.stringify(filterValues)));
    }

    function onHide() {
        // Cancel action, so reset the form
        resetFormVals();
        setShow(false);
    }

    function onShow() {
        resetFormVals();
        setShow(true);
    }

    const clearFilters = () => {
        _setCreatedByMe(false);
        setFormVals(JSON.parse(JSON.stringify({ ...initialReminderFilters, viewStatus: [] })));
    };

    function selectSavedFilter(selectedFilter: SearchFilterType) {
        setFilterSelection(selectedFilter);
        setFormVals((prev) => ({
            ...prev,
            clinic: selectedFilter?.clinic || null,
            PCP: selectedFilter?.PCP || null,
        }));
    }

    const filterMatch = React.useCallback(
        (filter: SearchFilterType) => {
            return (
                (formVals.clinic || formVals.PCP) &&
                isEqual(filter.clinic, formVals.clinic) &&
                isEqual(filter.PCP, formVals.PCP)
            );
        },
        [formVals.clinic, formVals.PCP]
    );

    React.useEffect(() => {
        // auto select saved filter on match
        if (!filterSelection) {
            savedFilters.some((filter: SearchFilterType) => {
                const matched = filterMatch(filter);
                if (matched) {
                    setFilterSelection(filter);
                }
                return matched;
            });
        }
        // clear selected filter on mismatch
        else if (!filterMatch(filterSelection)) {
            setFilterSelection(null);
        }
    }, [filterSelection, filterMatch, savedFilters]);

    function submit(e: SyntheticEvent) {
        e.preventDefault();
        resetPagination();
        resetSort();
        setFilterValues({ ...formVals });
        setCreatedByMe(_createdByMe);
        setShow(false);
    }

    return (
        <>
            <Button id="reminder-filters" variant="primary" onClick={onShow}>
                <i className="fa-solid fa-filter" /> Filters
            </Button>
            <BSModal
                className="reminders-filters-modal"
                show={show}
                onHide={onHide}
                aria-labelledby="reminders-filters-title"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="reminders-filters-title" role="heading" aria-level={1}>
                        Filters
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form>
                        <div className="row">
                            <div className="col col-md-5">
                                <Select
                                    isClearable
                                    options={filterOptions}
                                    value={filterSelection}
                                    placeholder="Saved Filters"
                                    label="Saved Filters"
                                    onChange={selectSavedFilter}
                                />
                            </div>
                        </div>
                        <hr className="my-3" />
                        <div className="col">
                            <div className="container">
                                <div className="row card">
                                    <div className="card-header">
                                        General
                                        {filterCount > 0 && (
                                            <span className="float-end badge bg-primary">
                                                {filterCount}
                                            </span>
                                        )}
                                    </div>
                                    <div className="card-body">
                                        <div className="row mb-2">
                                            <label
                                                htmlFor="reminders-clinic"
                                                className="col-3 col-form-label"
                                            >
                                                Clinic
                                            </label>
                                            <div className="col-9">
                                                <Select
                                                    isMulti
                                                    options={clinicList as ClinicType[]}
                                                    label="Clinic"
                                                    placeholder="Any Clinic"
                                                    value={formVals.clinic}
                                                    onChange={(x) =>
                                                        updateFormVal({
                                                            clinic: x as ClinicType[],
                                                        })
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className="row mb-2">
                                            <label
                                                htmlFor="reminders-pcp"
                                                className="col-3 col-form-label"
                                            >
                                                PCP
                                            </label>
                                            <div className="col-9">
                                                <Select
                                                    isMulti
                                                    options={_PCPList as PCPType[]}
                                                    label="PCP"
                                                    placeholder="Any PCP"
                                                    value={formVals.PCP}
                                                    onChange={(x) =>
                                                        updateFormVal({
                                                            PCP: x as PCPType[],
                                                        })
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className="row mb-2">
                                            <label
                                                htmlFor="reminders-status"
                                                className="col-3 col-form-label"
                                            >
                                                Status
                                            </label>
                                            <div className="col-9">
                                                <Select
                                                    isClearable
                                                    isMulti
                                                    closeMenuOnSelect={false}
                                                    options={statusOptions}
                                                    label="Status"
                                                    placeholder="Any status"
                                                    value={statusOptions.filter((x) =>
                                                        formVals.viewStatus.includes(x.value)
                                                    )}
                                                    onChange={(
                                                        x: { value: string; label: string }[]
                                                    ) =>
                                                        updateFormVal({
                                                            viewStatus: x.map(
                                                                (option) => option.value
                                                            ),
                                                        })
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className="row mb-2">
                                            <label
                                                htmlFor="reminders-status"
                                                className="col-3 col-form-label"
                                            >
                                                Action
                                            </label>
                                            <div className="col-9">
                                                <Select
                                                    isClearable
                                                    isMulti
                                                    closeMenuOnSelect={false}
                                                    classNamePrefix="option-with-description"
                                                    components={{ Option: OptionWithDescription }}
                                                    options={measureCodeOptions}
                                                    label="Action"
                                                    placeholder="Any action"
                                                    value={formVals.measureCode}
                                                    onChange={(val: OptionWithDescriptionType[]) =>
                                                        updateFormVal({
                                                            measureCode: val,
                                                        })
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className="row mb-2">
                                            <label
                                                htmlFor="reminders-pcp"
                                                className="col-3 col-form-label"
                                            >
                                                Type
                                            </label>
                                            <div className="col-9">
                                                <Select
                                                    options={createdByTypeOptions}
                                                    label="Type"
                                                    value={createdByTypeOptions.find(
                                                        (option) =>
                                                            option.value === formVals.createdByType
                                                    )}
                                                    onChange={(selected) =>
                                                        updateFormVal({
                                                            createdByType: selected!
                                                                .value as ReminderFilterType['createdByType'],
                                                        })
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className="row mb-2">
                                            <label
                                                htmlFor="due-date-start"
                                                className="col-3 col-form-label"
                                            >
                                                Due Date
                                            </label>
                                            <div className="col-9">
                                                <DatePicker
                                                    id="due-date-start"
                                                    name="due-date-start"
                                                    value={formVals.dueDateStart}
                                                    onChange={(val: null | Date) => {
                                                        updateFormVal({
                                                            dueDateStart: val
                                                                ? val.toISOString()
                                                                : null,
                                                        });
                                                    }}
                                                    maxDate={
                                                        formVals.dueDateEnd
                                                            ? new Date(formVals.dueDateEnd)
                                                            : undefined
                                                    }
                                                />
                                                <span className="ms-2 me-2">to</span>
                                                <DatePicker
                                                    id="due-date-end"
                                                    name="due-date-end"
                                                    value={formVals.dueDateEnd}
                                                    onChange={(val: null | Date) =>
                                                        updateFormVal({
                                                            dueDateEnd: val
                                                                ? val.toISOString()
                                                                : null,
                                                        })
                                                    }
                                                    minDate={
                                                        formVals.dueDateStart
                                                            ? new Date(formVals.dueDateStart)
                                                            : undefined
                                                    }
                                                />
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-12">
                                                <label
                                                    htmlFor="reminders-byme"
                                                    className="col-form-label"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        id="reminders-byme"
                                                        className="me-2"
                                                        checked={_createdByMe}
                                                        onChange={(e) =>
                                                            _setCreatedByMe(e.target.checked)
                                                        }
                                                    />
                                                    Created By Me
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </Modal.Body>
                <Modal.Footer className="text-end">
                    <button type="button" className="btn btn-secondary me-2" onClick={clearFilters}>
                        Clear
                    </button>
                    <button
                        id="reminder-search"
                        type="submit"
                        className="btn btn-primary"
                        onClick={submit}
                    >
                        Search
                    </button>
                </Modal.Footer>
            </BSModal>
        </>
    );
}
